import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircleIcon, Loader2Icon } from 'lucide-react';
import { AuthLayout } from '../../components/AuthLayout';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { TextInput } from '../../components/ui/TextInput';
import { Panel } from '../../components/ui/Panel';
import { StatusBadge } from '../../components/StatusBadge';
import { useAuth } from '../../contexts/AuthContext';
type Mode = 'login' | 'forgot';
const demoLogins = [{
  username: 'user',
  password: 'user123',
  role: 'Customer',
  detail: 'Priya Raman · Acme Internet, on Pro'
}, {
  username: 'admin',
  password: 'admin123',
  role: 'Staff',
  detail: 'Dev Ramachandran · superadmin console'
}];
export function Login() {
  const navigate = useNavigate();
  const {
    signIn
  } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('priya@acme.com');
  const [errors, setErrors] = useState<{
    username?: string;
    password?: string;
    email?: string;
  }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  function submitLogin(event: React.FormEvent) {
    event.preventDefault();
    const nextErrors: {
      username?: string;
      password?: string;
    } = {};
    if (!username.trim()) {
      nextErrors.username = 'Enter your username.';
    }
    if (!password) {
      nextErrors.password = 'Enter your password.';
    }
    setErrors(nextErrors);
    setFormError(null);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    setSubmitting(true);
    window.setTimeout(() => {
      setSubmitting(false);
      const session = signIn(username, password);
      if (!session) {
        setFormError('That username and password do not match an account.');
        return;
      }
      navigate(session.role === 'admin' ? '/admin' : '/app');
    }, 700);
  }
  function submitReset(event: React.FormEvent) {
    event.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setErrors({
        email: 'Enter a valid email address.'
      });
      return;
    }
    setErrors({});
    setSubmitting(true);
    window.setTimeout(() => {
      setSubmitting(false);
      setResetSent(true);
    }, 700);
  }
  const aside = <div className="space-y-4" data-id="element-1666" data-mp="7lkd0xcsdhwy">
      <Panel className="overflow-hidden" data-id="element-1667" data-mp="7lat9ric46zp">
        <div className="border-b border-line px-5 py-3" data-id="element-1668" data-mp="2tap1mv8erxb">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-soft" data-id="element-1669" data-mp="2rpuhx8x38zg">
            Demo accounts
          </h2>
        </div>
        <ul className="divide-y divide-line" data-id="element-1670" data-mp="van4sy14wr56">
          {demoLogins.map(account => <li key={account.username} className="px-5 py-3.5" data-id="element-1671" data-mp="qjn3lgkzfmwv">
              <div className="flex items-center justify-between gap-3" data-id="element-1672" data-mp="qhagkp0damcy">
                <span className="font-mono text-[13px] text-ink" data-id="element-1673" data-mp="297g8e4ykhs91">
                  {account.username} / {account.password}
                </span>
                <button type="button" onClick={() => {
              setUsername(account.username);
              setPassword(account.password);
              setFormError(null);
              setErrors({});
            }} className="rounded border border-line-strong px-2 py-1 text-[11px] font-medium text-ink-muted transition-colors duration-150 ease-snap hover:border-ink-soft hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-id="element-1674" data-mp="2i6tb8es6riks">
                  Use
                </button>
              </div>
              <p className="mt-1.5 text-xs text-ink-muted" data-id="element-1675" data-mp="3axbog8mmj0bx">
                <span className="font-medium text-ink" data-id="element-1676" data-mp="3ftscl2ttpep0">{account.role}</span> ·{' '}
                {account.detail}
              </p>
            </li>)}
        </ul>
      </Panel>

      <Panel className="p-5" data-id="element-1677" data-mp="2x9b9giyjzeso">
        <h2 className="text-sm font-semibold text-ink" data-id="element-1678" data-mp="2x808eqwhsl59">
          Waiting inside the customer account
        </h2>
        <p className="mt-1.5 text-[13px] leading-relaxed text-ink-muted" data-id="element-1679" data-mp="3bwwc6brinc02">
          A snapshot of the last sweep, run at 08:00 in your time zone.
        </p>
        <ul className="mt-4 space-y-3" data-id="element-1680" data-mp="2j8h1u2jbxhv2">
          <li className="flex items-center justify-between gap-3 border-b border-line pb-3" data-id="element-1681" data-mp="2j98w9a16x9jr">
            <span className="font-mono text-[13px] text-ink" data-id="element-1682" data-mp="2ebgtfkz0c30s">api.acme.com</span>
            <StatusBadge status="critical" label="Down 52 min" data-id="element-1683" data-mp="2t87s1s0uxapc" />
          </li>
          <li className="flex items-center justify-between gap-3 border-b border-line pb-3" data-id="element-1684" data-mp="2o7k5bijnvg0x">
            <span className="font-mono text-[13px] text-ink" data-id="element-1685" data-mp="8gfsnahrsnzb">
              shop.acme.com
            </span>
            <StatusBadge status="warning" label="9d remaining" data-id="element-1686" data-mp="3inqzorbpnhx" />
          </li>
          <li className="flex items-center justify-between gap-3" data-id="element-1687" data-mp="b1n97r31t8zp">
            <span className="text-[13px] text-ink" data-id="element-1688" data-mp="b3ghfatb819t">4 other monitors</span>
            <StatusBadge status="healthy" label="All healthy" data-id="element-1689" data-mp="g19kvw38ah6e" />
          </li>
        </ul>
      </Panel>
    </div>;
  if (mode === 'forgot') {
    return <AuthLayout title={resetSent ? 'Check your inbox' : 'Reset your password'} subtitle={resetSent ? `We sent a reset link to ${email}. It is good for one hour.` : 'We will email you a link to set a new password. Monitoring carries on in the meantime.'} aside={aside} footer={<button type="button" onClick={() => {
      setMode('login');
      setResetSent(false);
    }} className="font-medium text-brand hover:text-brand-hover" data-id="element-1691" data-mp="kzb54kce4mjg">
            Back to log in
          </button>} data-id="element-1690" data-mp="620sv8rxbcmo">
        {!resetSent && <form onSubmit={submitReset} className="space-y-4" noValidate data-id="element-1692" data-mp="1rdfxuz2pfkp1">
            <Field label="Email address" htmlFor="reset-email" error={errors.email} data-id="element-1693" data-mp="1rcmyij62rxgx">
              <TextInput id="reset-email" type="email" autoComplete="email" value={email} invalid={Boolean(errors.email)} onChange={event => setEmail(event.target.value)} data-id="element-1694" data-mp="1ichsodgj574v" />
            </Field>
            <Button type="submit" size="lg" className="w-full" disabled={submitting} data-id="element-1695" data-mp="13nbvev195qzm">
              {submitting && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1696" data-mp="unfbt7tpzibf" />}
              {submitting ? 'Sending link' : 'Email me a reset link'}
            </Button>
          </form>}
      </AuthLayout>;
  }
  return <AuthLayout title="Log in to Watchtower" subtitle="Customers manage their monitors; staff get the console. Same door, different room." aside={aside} footer={<>
          No account yet?{' '}
          <Link to="/signup" className="font-medium text-brand hover:text-brand-hover" data-id="element-1698" data-mp="kkjsuaq11w8n">
            Create one
          </Link>
        </>} data-id="element-1697" data-mp="alb01l6wennv">
      <form onSubmit={submitLogin} className="space-y-4" noValidate data-id="element-1699" data-mp="3o0qmdoeqad3p">
        {formError && <div role="alert" className="flex items-start gap-2.5 rounded-control border border-critical-border bg-critical-soft px-3.5 py-3" data-id="element-1700" data-mp="3sy8wvj8e90dt">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-critical" aria-hidden="true" data-id="element-1701" data-mp="6j7cvzav9umr" />
            <p className="text-[13px] text-critical-ink" data-id="element-1702" data-mp="358oasrgy1n0b">{formError}</p>
          </div>}

        <Field label="Username" htmlFor="login-username" error={errors.username} data-id="element-1703" data-mp="3a79cywfragre">
          <TextInput id="login-username" autoComplete="username" placeholder="user" className="font-mono" value={username} invalid={Boolean(errors.username)} onChange={event => setUsername(event.target.value)} data-id="element-1704" data-mp="s8uyt5634w4c" />
        </Field>

        <Field label="Password" htmlFor="login-password" error={errors.password} hint="Demo credentials are listed beside this form." data-id="element-1705" data-mp="x7y0cfmuwk7k">
          <TextInput id="login-password" type="password" autoComplete="current-password" className="font-mono" value={password} invalid={Boolean(errors.password)} onChange={event => setPassword(event.target.value)} data-id="element-1706" data-mp="175uezhf05xaf" />
        </Field>

        <div className="flex items-center justify-between gap-3" data-id="element-1707" data-mp="1uwpzdth4pq7n">
          <button type="button" onClick={() => setMode('forgot')} className="text-[13px] font-medium text-brand hover:text-brand-hover" data-id="element-1708" data-mp="1pyg5rnb1yxo5">
            Forgot your password?
          </button>
        </div>

        <Button type="submit" size="lg" className="w-full" disabled={submitting} data-id="element-1709" data-mp="2difopxsa06aj">
          {submitting && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-1710" data-mp="24k3tfiggskbr" />}
          {submitting ? 'Signing in' : 'Log in'}
        </Button>
      </form>
    </AuthLayout>;
}